import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QUIZ_QUESTIONS } from "@/quizData";
import { ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

export default function QuizSection({ onComplete }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [direction, setDirection] = useState(1);

  const totalSteps = QUIZ_QUESTIONS.length;
  const progress = ((step + 1) / totalSteps) * 100;
  const currentQ = QUIZ_QUESTIONS[step];
  const selected = answers[currentQ.id];

  const handleSelect = (opt) => {
    setAnswers(prev => ({
      ...prev,
      [currentQ.id]: { question_id: currentQ.id, answer: opt.value, score: opt.score }
    }));
  };

  const next = () => {
    if (!selected) return;
    if (step < totalSteps - 1) {
      setDirection(1);
      setStep(p => p + 1);
    } else {
      onComplete(Object.values(answers));
    }
  };

  const back = () => {
    if (step > 0) { setDirection(-1); setStep(p => p - 1); }
  };

  return (
    <div className="border border-zinc-800 rounded-xl p-8 bg-[#1a1a1c]/40" data-testid="quiz-section">
      {/* Progress */}
      <div className="flex items-center justify-between mb-6">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500">{step + 1} / {totalSteps}</span>
        <div className="flex-1 mx-4 h-1 bg-zinc-800 rounded-full overflow-hidden">
          <motion.div className="h-full bg-orange-500 rounded-full" animate={{ width: `${progress}%` }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            style={{ boxShadow: "0 0 8px rgba(249,115,22,0.4)" }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait" custom={direction}>
        <motion.div key={step}
          custom={direction}
          initial={{ x: direction > 0 ? 60 : -60, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: direction > 0 ? -60 : 60, opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <h3 className="text-xl font-bold tracking-tight mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="quiz-question-title">
            {currentQ.question}
          </h3>
          <p className="text-zinc-500 text-sm mb-5">{currentQ.subtitle}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {currentQ.options.map((opt, i) => {
              const isSelected = selected?.answer === opt.value;
              return (
                <button key={opt.value} onClick={() => handleSelect(opt)}
                  className={`text-left p-4 rounded-lg border transition-all duration-150 ${
                    isSelected
                      ? "border-orange-500 bg-orange-500/10"
                      : "border-zinc-800 hover:border-zinc-600 bg-transparent"
                  }`}
                  data-testid={`quiz-option-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                      isSelected ? "border-orange-500 bg-orange-500" : "border-zinc-600"
                    }`}>
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <div>
                      <span className={`text-sm font-semibold ${isSelected ? "text-white" : "text-zinc-400"}`}>{opt.label}</span>
                      <span className="text-xs text-zinc-600 block">{opt.desc}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-800/50">
        <button onClick={back} disabled={step === 0}
          className="flex items-center gap-1 text-sm text-zinc-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
          data-testid="quiz-back-button">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={next} disabled={!selected}
          className="flex items-center gap-2 bg-orange-500 text-white font-bold py-2.5 px-6 rounded-md text-sm transition-all hover:bg-orange-600 disabled:opacity-30 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(249,115,22,0.3)]"
          data-testid="quiz-next-button">
          {step === totalSteps - 1 ? (
            <><CheckCircle2 className="w-4 h-4" /> Submit</>
          ) : (
            <>Next <ChevronRight className="w-4 h-4" /></>
          )}
        </button>
      </div>
    </div>
  );
}
